/** Shared calorie maths (Mifflin-St Jeor) with safe-minimum limits for weight-loss targets. */
export type Sex = "male" | "female";
export const MIN_CALORIES: Record<Sex, number> = { male: 1500, female: 1200 };
export const MAX_DEFICIT_FRACTION = 0.25;
const KCAL_PER_KG = 7700;

export function bmrMifflin(sex: Sex, weightKg: number, heightCm: number, age: number): number {
  return 10 * weightKg + 6.25 * heightCm - 5 * age + (sex === "male" ? 5 : -161);
}
export const bmi = (weightKg: number, heightCm: number) => (heightCm > 0 ? weightKg / Math.pow(heightCm / 100, 2) : 0);

export interface BodyCheck { error: string; underweight: boolean }
export function checkBody(age: number, heightCm: number, weightKg: number): BodyCheck {
  if (!(age > 0)) return { error: "Enter your age.", underweight: false };
  if (age < 18) return { error: "This calculator is for adults (18+). Children and teenagers have different needs: ask a doctor.", underweight: false };
  if (age > 110) return { error: "Enter an age up to 110.", underweight: false };
  if (!(heightCm >= 100 && heightCm <= 250)) return { error: "Enter a height between 100 and 250 cm (about 3 ft 3 in to 8 ft 2 in).", underweight: false };
  if (!(weightKg >= 30 && weightKg <= 300)) return { error: "Enter a weight between 30 and 300 kg (about 66 to 660 lb).", underweight: false };
  return { error: "", underweight: bmi(weightKg, heightCm) < 18.5 };
}

export interface Target { label: string; kcal: number; deficit: number; kind: "loss" | "maintain" | "gain"; clamped: boolean; unavailable: boolean; kgPerWeek: number }

/** Loss targets never go below the safe minimum or more than 25% under maintenance; if maintenance itself is at or near the minimum, loss is unavailable. */
export function goalTargets(tdee: number, sex: Sex, underweight: boolean): Target[] {
  const floor = MIN_CALORIES[sex];
  const loss = (label: string, wanted: number): Target => {
    const limit = Math.max(floor, tdee * (1 - MAX_DEFICIT_FRACTION));
    const target = Math.max(tdee - wanted, limit);
    const clamped = target > tdee - wanted + 0.5;
    const unavailable = underweight || tdee - target < 100;
    return { label, kcal: unavailable ? tdee : target, deficit: unavailable ? 0 : tdee - target, kind: "loss", clamped: clamped && !unavailable, unavailable, kgPerWeek: unavailable ? 0 : ((tdee - target) * 7) / KCAL_PER_KG };
  };
  const gain = (label: string, extra: number): Target => ({ label, kcal: tdee + extra, deficit: -extra, kind: "gain", clamped: false, unavailable: false, kgPerWeek: (extra * 7) / KCAL_PER_KG });
  return [
    loss("Aggressive Loss", 1000), loss("Moderate Loss", 500),
    { label: "Maintain Weight", kcal: tdee, deficit: 0, kind: "maintain", clamped: false, unavailable: false, kgPerWeek: 0 },
    gain("Moderate Gain", 500), gain("Aggressive Gain", 1000),
  ];
}

export function weeklyNote(t: Target): string {
  if (t.kind === "maintain") return "No change";
  const kg = Math.abs(t.kgPerWeek), lb = kg * 2.2046;
  const sign = t.kind === "loss" ? "lose" : "gain";
  return `about ${kg.toFixed(2)} kg (${lb.toFixed(1)} lb) ${sign === "lose" ? "lost" : "gained"} per week`;
}

export interface DisplayTarget extends Target { shownLabel: string; /** Set on a loss row that stands in for a faster tier the 25% cap left within MERGE_WITHIN_KCAL of it. */ mergedNote?: string }
/** The pounds-a-week figure a row shows (one decimal), used to tell whether two rows read the same. */
export const lbLabel = (t: Target) => (Math.abs(t.kgPerWeek) * 2.2046).toFixed(1);
/** Two loss tiers closer than this AND reading the same lb a week are one choice, not two (B-0138). The male sample (1752 vs 1836 cal, 1.2 vs 1.0 lb a week) stays as two rows. */
export const MERGE_WITHIN_KCAL = 100;

/** Rows to show: identical clamped loss rows become one, and capped rows are labelled "(capped)". */
export function displayTargets(targets: Target[]): DisplayTarget[] {
  const out: DisplayTarget[] = [];
  for (const t of targets) {
    const prev = out[out.length - 1];
    if (t.kind === "loss" && t.clamped && prev && prev.kind === "loss" && prev.clamped && Math.round(prev.kcal) === Math.round(t.kcal)) {
      out[out.length - 1] = { ...prev, shownLabel: "Weight Loss (capped)" };
      continue;
    }
    // The cap can leave the faster tier only a few calories under the gentler one (both about the same lb a week): keep the gentler, uncapped row and say why.
    if (t.kind === "loss" && !t.unavailable && prev && prev.kind === "loss" && !prev.unavailable && prev.clamped && !t.clamped && Math.abs(t.kcal - prev.kcal) < MERGE_WITHIN_KCAL && lbLabel(t) === lbLabel(prev)) {
      out[out.length - 1] = { ...t, shownLabel: "Weight Loss", mergedNote: `A faster loss tier would land within ${MERGE_WITHIN_KCAL} cal of this one because of the safe cap (at most 25% under maintenance), so it is shown once.` };
      continue;
    }
    out.push({ ...t, shownLabel: t.clamped ? `${t.label} (capped)` : t.label });
  }
  return out;
}
